import { useCallback, useRef } from 'react';

import { Wallet, getWalletBalances } from '@/utils/fetch-wallet-balance';
import { logger } from '@/utils/logging';
import { create } from 'zustand';

import { Product, useJurisdictionStore } from './use-jurisdiction';
import { useResumeEffect } from './use-resume';
import { SocialCurrency, useSocialCurrencyStore } from './use-social-currency';
import { useUserStore } from './use-user-store';

type WalletStore = {
    balance: number;
    bonus: number;
    total: number;
    realMoneyTotal: number;
    betrBucks: number;
    walletLoading: boolean;
    wallet: null | Wallet;
    actions: {
        setBonus: (value: number) => void;
        loadUserWallet: () => Promise<void>;
        setupGuestWallet: (wallet: Wallet) => void;
    };
};

export const useWalletStore = create<WalletStore>()((set, get) => {
    return {
        balance: 0,
        bonus: 0,
        total: 0,
        realMoneyTotal: 0,
        betrBucks: 0,
        walletLoading: false,
        wallet: null,
        actions: {
            setBonus: (value: number) => set({ bonus: value }),
            loadUserWallet: async () => {
                if (get().walletLoading) {
                    return;
                }
                const { actions, guest } = useUserStore.getState();
                if (actions.sessionHasExpired() || guest) {
                    logger.debug('[wallet] No session or guest user, not loading wallet');
                    return;
                }
                set({ walletLoading: true });
                try {
                    const wallets = await getWalletBalances();
                    const product = useJurisdictionStore.getState().product;
                    const socialCurrency = useSocialCurrencyStore.getState().activeCurrency;

                    let wallet = wallets.fantasy_wallet;
                    if (product === Product.Sweeps || product === Product.Casino) {
                        wallet =
                            socialCurrency === SocialCurrency.CASH
                                ? wallets.sweeps_cash_wallet
                                : wallets.sweeps_coin_wallet;
                    }

                    const total = Number(wallet.total);
                    const realMoneyTotal = Number(wallet.bonus_amount) + Number(wallet.real_amount);
                    const balance = Number(wallet.real_amount);
                    const bonus = Number(wallet.bonus_amount);
                    const betrBucks = Number(wallet.free_amount);
                    set({
                        wallet,
                        total,
                        realMoneyTotal,
                        balance,
                        bonus,
                        betrBucks,
                    });
                } catch (e) {
                    logger.error('[wallet] Error loading user wallet', e);
                }
                set({ walletLoading: false });
            },
            setupGuestWallet: (wallet: Wallet) => {
                set({
                    wallet,
                    total: Number(wallet.total),
                    realMoneyTotal: Number(wallet.bonus_amount) + Number(wallet.real_amount),
                    balance: Number(wallet.real_amount),
                    bonus: Number(wallet.bonus_amount),
                    betrBucks: Number(wallet.free_amount),
                });
            },
        },
    };
});

type WalletOptions = {
    staleTimeMs: number;
};

export const useWallet = (opts?: WalletOptions) => {
    const staleTimeMs = opts?.staleTimeMs;

    const store = useWalletStore();

    const lastFetch = useRef(0);

    useResumeEffect(
        useCallback(() => {
            const now = Date.now();
            // Prevent over-fetching the wallet if the stale time has not passed
            if (staleTimeMs && now - lastFetch.current < staleTimeMs) {
                return;
            }
            lastFetch.current = now;
            store.actions.loadUserWallet();
        }, [staleTimeMs, store.actions])
    );

    return store;
};
